import React, {useRef, useState} from 'react';
import styles from './styles.module.scss';
import {
    Button,
    Text,
    DropdownV2,
    Flex,
    noop,
    PriorityIcon,
    Icon
} from '@priority-software/priority-style-react';
import {partsStore} from '../PartsStore';
import {InputTrigger} from '../../../_legacy/FieldSearch/components/InputTrigger';
import {useKeyboardNavigation} from '../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {SearchOptions} from '../../../components/Common/FieldSearch';
import {Direction} from '../../../_legacy/library/utils/search';
import {FieldSearchDropdownProps} from '../../../components/Common/FieldSearch/types/DropdownProps';
import {isRTL} from '../../../Localization';
import texts from '../texts';
import {observer} from 'mobx-react-lite';
import {sendMonitor} from '../../../utils/monitoring';
import {PARTS_SEARCH_MONITOR, PARTS_SEARCH_MONITOR_FAMILYFILTER} from '../consts';

const initState = {
    results: [],
    _results: [],
    isSearchable: false,
    isScrollable: false,
    isSearching: false,
    isScrolling: false,
    isNoResults: false,
    searchQuery: '',
    isZoom: false,
    isMulti: false,
    isLoading: true,
    focusedIndex: -1,
    direction: 'auto' as Direction,
    stackCursor: '0',
    searchStart: '0'
};

const FamilySelect = observer(() => {
    const {families, selectedFamilies, setSelectedFamilies} = partsStore;
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState([]);
    const [query, setQuery] = useState('');

    const inputRef = useRef<HTMLInputElement>();

    const onOpen = (e?: Event) => {
        e?.stopPropagation();
        setSelected(selectedFamilies || []);
        setIsOpen(true);
        sendMonitor(`${PARTS_SEARCH_MONITOR}${PARTS_SEARCH_MONITOR_FAMILYFILTER}`);
    };

    const onClose = () => {
        setSelected([]);
        setIsOpen(false);
        setQuery('');
    };

    const onSpace = () => {
        const item = families[focusedIndex];
        if (item) {
            handleSelectItem(item);
        }
    };

    const handleConfirm = () => {
        setSelectedFamilies(selected);
        onClose();
    };

    const handleSelectItem = item => {
        setSelected(prev => {
            const index = prev.findIndex(itm => itm === item.retval);
            if (index > -1) {
                const arr = [...prev];
                arr.splice(index, 1);
                return arr;
            } else {
                return [...prev, item.retval];
            }
        });
    };

    const filtered = families?.filter(
        fam =>
            fam.string1.toLowerCase().includes(query.toLowerCase()) ||
            fam.string2.toLowerCase().includes(query.toLowerCase()) ||
            fam.string3.toLowerCase().includes(query.toLowerCase())
    );

    const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
        initialFocusedIndex: -1,
        listLength: filtered?.length || 0,
        pageSize: 1,
        onSpace: isOpen ? undefined : onSpace,
        onSelect: handleConfirm
    });

    return (
        <ChooseDropDown
            {...initState}
            direction={isRTL() ? 'rtl' : 'ltr'}
            results={filtered}
            searchQuery={query}
            onSearchByChange={noop}
            onSortByChange={noop}
            onScroll={noop}
            onKeyDown={handleKeyboardNavigation}
            focusedIndex={focusedIndex}
            onSearchFromBeginningChange={noop}
            onIgnoreCaseChange={noop}
            isItemSelected={item => selected?.find(itm => item.retval === itm)}
            isMulti={true}
            onSelectItem={handleSelectItem}
            onMultiSelect={handleConfirm}
            onSearchChange={setQuery}
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            onClear={() => setSelected([])}
            selectedCount={selected?.length}
            inputProps={{
                // onFocus: onOpen,
                inputRef: inputRef,
                placeholder: isOpen
                    ? texts.selectFamily
                    : !!selectedFamilies?.length
                    ? texts.selected?.replace('X', selectedFamilies?.length)
                    : texts.allFamilies
            }}
        />
    );
});

interface WrapperProps extends FieldSearchDropdownProps {
    selectedCount: number;
    onClear: () => void;
}
const ChooseDropDown = (props: WrapperProps) => {
    const {isOpen, onClose, onKeyDown, onClear, triggerRef, results, onOpen, inputProps} = props;
    return !isOpen ? (
        <FakeInput text={inputProps.placeholder} handleArrowClick={onOpen} />
    ) : (
        <DropdownV2
            isOpen={isOpen}
            width="fullWidth"
            openingSide="leftSide"
            style={{width: 220}}
            contentStyle={{width: 440}}
            onClose={onClose}
            onKeyDown={onKeyDown}
            trigger={<InputTrigger {...props} />}
            triggerRef={triggerRef}
        >
            <div>
                {!!results?.length ? (
                    <>
                        <SearchOptions {...props} />
                    </>
                ) : (
                    <Text
                        componentClasses={styles.familyEmptyState}
                        skin="paragraph-2"
                        textColor="secondary-400"
                    >
                        {texts.emptyState}
                    </Text>
                )}
                <Footer
                    onConfirm={props.onMultiSelect}
                    onClear={onClear}
                    selectedCount={props.selectedCount}
                />
            </div>
        </DropdownV2>
    );
};
interface FooterProps {
    onConfirm: () => void;
    onClear: () => void;
    selectedCount: number;
}
const Footer = ({onConfirm, onClear, selectedCount}: FooterProps) => {
    const handleClear = (e: Event) => {
        e.stopPropagation();
        onClear();
    };
    return (
        <Flex componentClasses={styles.familySelectFooter}>
            <Flex justifyContent="flex-start">
                <Text
                    componentClasses={styles.footerSelected}
                    skin="label-2"
                    textColor="secondary-400"
                >
                    {texts.selected?.replace('X', selectedCount)}
                </Text>
                {!!selectedCount && (
                    <Button
                        onClick={handleClear}
                        componentClasses={styles.footerClear}
                        skin="text"
                        size="small"
                    >
                        {texts.clear}
                    </Button>
                )}
            </Flex>
            <Button onClick={onConfirm} width="regularFixed" size="small">
                {texts.add}
            </Button>
        </Flex>
    );
};

const FakeInput = ({handleArrowClick, text}) => (
    <div className={styles.fakeInput} onClick={handleArrowClick}>
        <Text skin="paragraph-1" textColor="grey-light">
            {text}
        </Text>
        <Icon icon={PriorityIcon.arrowDropDown} onClick={() => {}} skin="standart" />
    </div>
);
export default FamilySelect;
